import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

const PENDING_BADGE_ORANGE = '#FFA500';

/** No extra padding on container: create page content already has paddingHorizontal, so we match friends tab (single padding layer). */
export const getCreateParticipantsStepStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
  /** Wrapper for grid content (FlashList); needs flex: 1 so list gets bounded height and can scroll. */
  gridContentWrapper: {
    flex: 1,
    width: '100%',
    minHeight: 0,
  },
  sectionTitle: {
    fontSize: Platform.OS === 'web' ? 18 : 16,
    fontWeight: '700',
    color: theme.black,
    marginBottom: Platform.OS === 'web' ? 16 : 10,
    letterSpacing: 0.5,
  },
  sectionSubtitle: {
    fontSize: Platform.OS === 'web' ? 15 : 14,
    color: theme.gray[600],
    marginBottom: Platform.OS === 'web' ? 20 : 12,
  },
  toggleWrapper: {
    marginBottom: Platform.OS === 'web' ? 16 : 12,
  },
  // "Post to anyone" open-challenge entry — a full-width call-out above the Friends/Groups toggle.
  openBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: theme.primary + '1F',
    borderWidth: 1,
    borderColor: theme.primary + '55',
    borderRadius: theme.borderRadius.lg,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: Platform.OS === 'web' ? 16 : 12,
  },
  openBoxPressed: {
    opacity: 0.8,
  },
  openBoxHover: {
    borderColor: theme.primary,
  },
  openBoxIcon: {
    fontSize: 24,
  },
  openBoxText: {
    flex: 1,
    gap: 2,
  },
  openBoxTitle: {
    fontSize: Platform.OS === 'web' ? 16 : 15,
    fontWeight: '800',
    color: theme.black,
  },
  openBoxSub: {
    fontSize: 12,
    fontWeight: '500',
    color: theme.gray[600],
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  pendingListContainer: {
    gap: Platform.OS === 'web' ? 12 : 8,
    marginTop: Platform.OS === 'web' ? 8 : 4,
  },
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: theme.gray[500],
  },
  emptyContainer: {
    padding: Platform.OS === 'web' ? 20 : 16,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: Platform.OS === 'web' ? 16 : 14,
    color: theme.gray[500],
  },
  friendCard: {
    backgroundColor: theme.background.card, // Adapts to dark mode
    borderRadius: Platform.OS === 'web' ? 12 : 10,
    padding: Platform.OS === 'web' ? 16 : 12,
    marginBottom: Platform.OS === 'web' ? 12 : 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 2,
    borderColor: theme.gray[200],
    ...(Platform.OS === 'web'
      ? { boxShadow: '0px 2px 3.84px rgba(0,0,0,0.1)' as const }
      : {
          shadowColor: theme.shadow,
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 3.84,
          elevation: 5,
        }),
  },
  selectedFriendCard: {
    borderColor: theme.primary,
    borderWidth: 2,
  },
  friendInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  friendInfoText: {
    flex: 1,
  },
  friendAvatar: {
    fontSize: Platform.OS === 'web' ? 24 : 20,
    marginRight: Platform.OS === 'web' ? 12 : 10,
  },
  friendAvatarImage: {
    width: Platform.OS === 'web' ? 40 : 36,
    height: Platform.OS === 'web' ? 40 : 36,
    borderRadius: Platform.OS === 'web' ? 20 : 18,
    backgroundColor: theme.gray[200],
    marginRight: Platform.OS === 'web' ? 12 : 10,
  },
  friendAvatarContainer: {
    width: Platform.OS === 'web' ? 40 : 36,
    height: Platform.OS === 'web' ? 40 : 36,
    borderRadius: Platform.OS === 'web' ? 20 : 18,
    backgroundColor: theme.primary + '20',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: Platform.OS === 'web' ? 12 : 10,
  },
  friendName: {
    fontSize: Platform.OS === 'web' ? 16 : 14,
    fontWeight: '600',
    color: theme.black,
  },
  friendNameSelected: {
    fontSize: Platform.OS === 'web' ? 16 : 14,
    fontWeight: '700',
    color: theme.primary,
  },
  friendEmail: {
    fontSize: Platform.OS === 'web' ? 12 : 11,
    color: theme.gray[500],
    marginTop: 2,
  },
  friendSelected: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  friendSelectedText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.white,
  },
  pendingSection: {
    marginTop: Platform.OS === 'web' ? 24 : 20,
    paddingTop: Platform.OS === 'web' ? 16 : 12,
    borderTopWidth: 1,
    borderTopColor: theme.gray[200],
  },
  pendingSectionTitle: {
    fontSize: Platform.OS === 'web' ? 14 : 13,
    fontWeight: '600',
    color: theme.gray[500],
    marginBottom: Platform.OS === 'web' ? 12 : 10,
  },
  friendCardDisabled: {
    opacity: 0.6,
    borderColor: theme.gray[300],
  },
  pendingBadge: {
    backgroundColor: PENDING_BADGE_ORANGE,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 10,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: Platform.OS === 'web' ? 8 : 6,
  },
  pendingBadgeText: {
    fontSize: Platform.OS === 'web' ? 12 : 11,
    fontWeight: '600',
    color: theme.white,
  },
});